import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Members from "../../apps/company/pages/Members";
import AdminSettings from "../../apps/company/pages/AdminSettings";
import Page from "../Page";
import NotFound from "../../components/NotFound";
import Sidebar from "../../apps/company/components/layout/sidebar";
import styles from "apps/company/styles/company.module.scss";
import { Provider } from "react-redux";
import { store } from "apps/company/redux/store";
import Integrations from "apps/company/pages/Integrations";
import CompanyDashboard from "apps/company/pages/CompanyDashboard";
import { usePlainBackground } from "./plain-background";
import {
  CompanyDashboardAccessProvider,
  useCompanyDashboardAccess,
} from "apps/company/api/rest/company-dashboard-access";

/**
 * The `client_dashboard` flag has to gate the route, not just the sidebar
 * link: hiding the link alone still serves the page to a typed URL or a
 * bookmark, and the flag is the only rollout control this feature has.
 *
 * While the answer is still in flight the route renders nothing rather than
 * redirecting, so an entitled admin opening a deep link lands on the page
 * instead of being bounced home by a race.
 */
const GatedCompanyDashboard = () => {
  const { enabled, pending } = useCompanyDashboardAccess();
  if (pending) return null;
  if (!enabled) return <Navigate to="/" replace />;
  return <CompanyDashboard />;
};

/**
 * Where entering the portal lands: the WONE Dashboard for a company that has
 * it, Members for everyone else. Waits for the access answer rather than
 * landing on Members first, so an entitled admin never sees it flash by.
 */
const PortalLanding = () => {
  const { enabled, pending } = useCompanyDashboardAccess();
  if (pending) return null;
  return (
    <Navigate
      to={enabled ? "/client/company-dashboard" : "/client/members"}
      replace
    />
  );
};

const CorporateRoutes = [
  {
    Title: "Company Dashboard",
    Path: "/company-dashboard/*",
    Component: GatedCompanyDashboard,
  },
  {
    Title: "Members",
    Path: "/members",
    Component: Members,
  },

  {
    Title: "Integrations",
    Path: "/integrations",
    Component: Integrations,
  },
  {
    Title: "Admin Setting",
    Path: "/admin-settings",
    Component: AdminSettings,
  },
  {
    Title: "Page Not Found",
    Component: NotFound,
  },
];
const CorporateShell = () => {
  usePlainBackground();
  return (
    <Provider store={store}>
      <div className={styles["corporate-container"]}>
        <Sidebar />
        <Routes>
          <Route index element={<PortalLanding />} />
          {/* The retired Tableau dashboard's address, kept for bookmarks. */}
          <Route
            path="/dashboard"
            element={<Navigate to="/client" replace />}
          />
          {CorporateRoutes.map((Routes, index: number) => {
            return (
              <Route
                key={index}
                path={Routes.Path}
                element={
                  <Page title={Routes.Title}>
                    <Routes.Component />
                  </Page>
                }
              />
            );
          })}
          <Route path="/*" element={<Navigate to="/" />} />
        </Routes>
      </div>
    </Provider>
  );
};

const Corporate = () => (
  <CompanyDashboardAccessProvider>
    <CorporateShell />
  </CompanyDashboardAccessProvider>
);

export default Corporate;
